// src/features/admin-panel/_vm/use-events.ts
// src/hooks/useEvents/useEvents.js
import { useCallback, useMemo } from "react";
import {
  fetchClient,
  publicRqClient,
  rqClient,
} from "../../shared/api/instance";
// import { ApiSchemas } from "../shared/api/schema";
import {
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { queryClient } from "../../shared/api/query-client";
import { useMutation } from "@tanstack/react-query";
import { keepPreviousData } from "@tanstack/react-query";
import { PAGE_SIZE } from "../../constants";

// Базовые параметры для всех хуков событий
const createEventsQueryConfig = (enabled = true) => ({
  initialPageParam: 0,
  pageParamName: "page",
  getNextPageParam: (lastPage, _, lastPageParams) =>
    lastPage.data?.totalPages &&
    Number(lastPageParams) < lastPage.data.totalPages - 1
      ? Number(lastPageParams) + 1
      : undefined,
  placeholderData: keepPreviousData,
  enabled,
});

export function useGetPublicEvent({ eventId, enabled = true }) {
  return publicRqClient.useQuery(
    "get",
    "/events/v1/events/{id}/public",
    {
      params: {
        path: { id: eventId },
      },
    },
    {
      enabled: enabled && !!eventId,
      staleTime: 5 * 60 * 1000, // 5 минут
      gcTime: 10 * 60 * 1000, // 10 минут
    }
  );
}

export function useGetEventWithJoinedStatus({
  eventId,
  customerId,
  enabled = true,
}) {
  return rqClient.useQuery(
    "get",
    "/events/v1/events/{id}",
    {
      params: {
        path: { id: eventId },
        query: { customerId },
      },
    },
    {
      enabled: enabled && !!(eventId && customerId),
      staleTime: 2 * 60 * 1000, // 2 минуты
      gcTime: 5 * 60 * 1000, // 5 минут
    }
  );
}

export function useGetEvent({ eventId, customerId, enabled = true }) {
  const publicEvent = useGetPublicEvent({
    eventId,
    enabled: enabled && !customerId,
  });

  const authenticatedEvent = useGetEventWithJoinedStatus({
    eventId,
    customerId,
    enabled: enabled && !!customerId,
  });

  return customerId ? authenticatedEvent : publicEvent;
}

export function useInvalidateEvent() {
  const queryClient = useQueryClient();

  return (eventId, customerId = null) => {
    const queryKey = customerId
      ? rqClient.queryOptions("get", "/events/v1/events/{id}", {
          params: {
            path: { id: eventId },
            query: { customerId },
          },
        }).queryKey
      : publicRqClient.queryOptions("get", "/events/v1/events/{id}", {
          params: {
            path: { id: eventId },
            query: { customerId: 0 },
          },
        }).queryKey;

    queryClient.invalidateQueries({ queryKey });
  };
}

// Хук для публичных событий
export function useGetPublicEvents({
  size = PAGE_SIZE,
  sort,
  status,
  tier,
  region,
  city,
  enabled = true,
}) {
  const queryParams = {
    status: status && status.length > 0 ? status.join(",") : undefined,
    tier: tier && tier.length > 0 ? tier.join(",") : undefined,
    region,
    city: city !== "ALL" ? city : undefined,
    page: 0,
    size,
    sort: sort ? [sort] : undefined,
  };

  const { fetchNextPage, data, isFetchingNextPage, isPending, hasNextPage } =
    publicRqClient.useInfiniteQuery(
      "get",
      "/events/v1/events",
      {
        params: {
          query: queryParams,
        },
      },
      createEventsQueryConfig(enabled && !!(region && city))
    );

  const cursorRef = useCallback(
    (el) => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 }
      );

      if (el) {
        observer.observe(el);
        return () => {
          observer.disconnect();
        };
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const events = useMemo(
    () =>
      data?.pages.flatMap(
        (page) =>
          page.data?.content?.filter(
            (event, index, self) =>
              self.findIndex((e) => e.id === event.id) === index
          ) || []
      ) || [],
    [data]
  );

  return {
    events,
    fetchNextPage,
    isFetchingNextPage,
    isPending,
    hasNextPage,
    cursorRef,
    currentPage: data?.pages.length ? data.pages.length - 1 : 0,
    totalPages: data?.pages[0]?.data?.totalPages || 0,
    totalEvents: data?.pages[0]?.data?.totalElements || 0,
    hasEvents: events.length > 0,
  };
}

// Хук для аутентифицированных пользователей (с joined status)
export function useGetEventsWithJoinedStatus({
  size = PAGE_SIZE,
  sort,
  status,
  tier,
  region,
  city,
  customerId,
  enabled = true,
}) {
  const queryParams = {
    customerId,
    ...(status && status.length > 0 ? { status: status.join(",") } : {}),
    ...(tier && tier.length > 0 ? { tier: tier.join(",") } : {}),
    ...(region ? { region } : {}),
    ...(city ? { city } : {}),
    page: 0,
    size,
    sort: sort ? [sort] : undefined,
  };

  const { fetchNextPage, data, isFetchingNextPage, isPending, hasNextPage } =
    rqClient.useInfiniteQuery(
      "get",
      "/events/v1/events/with-joined-status",
      {
        params: {
          query: queryParams,
        },
      },
      createEventsQueryConfig(enabled && !!(customerId && region && city))
    );

  const cursorRef = useCallback(
    (el) => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 }
      );

      if (el) {
        observer.observe(el);
        return () => {
          observer.disconnect();
        };
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const events = useMemo(
    () =>
      data?.pages.flatMap(
        (page) =>
          page.data?.content?.filter(
            (event, index, self) =>
              self.findIndex((e) => e.id === event.id) === index
          ) || []
      ) || [],
    [data]
  );

  return {
    events,
    fetchNextPage,
    isFetchingNextPage,
    isPending,
    hasNextPage,
    cursorRef,
    currentPage: data?.pages.length ? data.pages.length - 1 : 0,
    totalPages: data?.pages[0]?.data?.totalPages || 0,
    totalEvents: data?.pages[0]?.data?.totalElements || 0,
    hasEvents: events.length > 0,
  };
}

// Умный хук, который использует enabled для выбора активного хука
export function useGetEvents(options) {
  const { customerId, enabled = true, ...restOptions } = options;

  const publicEvents = useGetPublicEvents({
    ...restOptions,
    enabled: enabled && !customerId, // Включаем публичный хук только если нет customerId
  });

  const authenticatedEvents = useGetEventsWithJoinedStatus({
    customerId,
    ...restOptions,
    enabled: enabled && !!customerId, // Включаем аутентифицированный хук только если есть customerId
  });

  // Возвращаем данные активного хука
  return customerId ? authenticatedEvents : publicEvents;
}

// Хук для получения только присоединенных событий (групповые классы)
export function useGetJoinedOnlyEvents({
  size = PAGE_SIZE,
  sort,
  status,
  region,
  city,
  customerId,
  enabled = true,
}) {
  const queryParams = {
    customerId,
    ...(status && status.length > 0 ? { status: status.join(",") } : {}),
    ...(region ? { region } : {}),
    ...(city && city !== "ALL" ? { city } : {}),
    page: 0,
    size,
    sort: sort ? [sort] : undefined,
  };

  const {
    fetchNextPage,
    data,
    isFetchingNextPage,
    isPending,
    hasNextPage,
    error,
  } = rqClient.useInfiniteQuery(
    "get",
    "/events/v1/events/joined-only",
    {
      params: {
        query: queryParams,
      },
    },
    {
      ...createEventsQueryConfig(enabled && !!(customerId && region)),
      retry: 1,
    }
  );

  const cursorRef = useCallback(
    (el) => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 }
      );

      if (el) {
        observer.observe(el);
        return () => {
          observer.disconnect();
        };
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const events = useMemo(
    () =>
      data?.pages.flatMap(
        (page) =>
          page.data?.content?.filter(
            (event, index, self) =>
              self.findIndex((e) => e.id === event.id) === index
          ) || []
      ) || [],
    [data]
  );

  return {
    events,
    fetchNextPage,
    isFetchingNextPage,
    isPending,
    hasNextPage,
    cursorRef,
    error,
    currentPage: data?.pages.length ? data.pages.length - 1 : 0,
    totalPages: data?.pages[0]?.data?.totalPages || 0,
    totalEvents: data?.pages[0]?.data?.totalElements || 0,
    hasEvents: events.length > 0,
  };
}

// Хук для получения bookings (1:1 сессии)
export function useGetBookings({
  size = PAGE_SIZE,
  sort,
  status,
  region,
  customerId,
  partnerId,
  enabled = true,
}) {
  const queryParams = {
    customerId,
    ...(partnerId ? { partnerId } : {}),
    ...(region ? { region } : {}),
    ...(status && status.length > 0 ? { status: status.join(",") } : {}),
    page: 0,
    size,
    sort: sort ? [sort] : undefined,
  };

  const {
    fetchNextPage,
    data,
    isFetchingNextPage,
    isPending,
    hasNextPage,
    error,
  } = rqClient.useInfiniteQuery(
    "get",
    "/bookings/v1/bookings",
    {
      params: {
        query: queryParams,
      },
    },
    {
      ...createEventsQueryConfig(enabled && !!(customerId && region && status)),
      retry: 1,
    }
  );

  const cursorRef = useCallback(
    (el) => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 }
      );

      if (el) {
        observer.observe(el);
        return () => {
          observer.disconnect();
        };
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const bookings = useMemo(
    () =>
      data?.pages.flatMap(
        (page) =>
          page.data?.content?.filter(
            (booking, index, self) =>
              self.findIndex((b) => b.id === booking.id) === index
          ) || []
      ) || [],
    [data]
  );

  return {
    bookings,
    fetchNextPage,
    isFetchingNextPage,
    isPending,
    hasNextPage,
    cursorRef,
    error,
    currentPage: data?.pages.length ? data.pages.length - 1 : 0,
    totalPages: data?.pages[0]?.data?.totalPages || 0,
    totalBookings: data?.pages[0]?.data?.totalElements || 0,
    hasBookings: bookings.length > 0,
  };
}

// Хук для получения upcoming bookings
export function useGetUpcomingBookings({
  size = PAGE_SIZE,
  region,
  customerId,
  enabled = true,
}) {
  return useGetBookings({
    size,
    region,
    customerId,
    status: ["PAID", "ACTIVE"],
    sort: "start,asc",
    enabled,
  });
}

// Хук для получения past bookings
export function useGetPastBookings({
  size = PAGE_SIZE,
  region,
  customerId,
  enabled = true,
}) {
  return useGetBookings({
    size,
    region,
    customerId,
    status: ["COMPLETED", "CANCELLED"],
    sort: "start,desc",
    enabled,
  });
}

export function useGetSeriesById(id, customerId) {
  return useQuery(
    rqClient.queryOptions(
      "get",
      "/events/v1/series/{id}",
      {
        params: {
          path: { id },
          query: { customerId },
        },
      },
      {
        enabled: !!customerId,
      }
    )
  );
}

// Хук для инвалидации
export function useInvalidateSeries() {
  return ({ user, region, city, sort }) => {
    queryClient.invalidateQueries({
      queryKey: rqClient.queryOptions("get", "/events/v1/series", {
        params: {
          query: {
            region, // или другой дефолтный ID, если нужно
            city,
            sort: sort || "start,desc",
          },
        },
      }).queryKey,
    });
  };
}

export function useGetSeries({ user, region, city, sort }) {
  console.log("useGetSeries", user, region, city, sort);
  return rqClient.useQuery(
    "get",
    "/events/v1/series",
    {
      params: {
        query: {
          region, // или другой дефолтный ID, если нужно
          city,
          sort: sort || "start,asc",
        },
      },
    },
    {
      enabled: !!user,
    }
  );
}

export function useGetSeriesPublic({ user, region, city, sort }) {
  return publicRqClient.useQuery(
    "get",
    "/events/v1/series",
    {
      params: {
        query: {
          region, // или другой дефолтный ID, если нужно
          city,
          sort: sort || "start,asc",
        },
      },
    },
    {
      // enabled: !user,
    }
  );
}

export function useGetEventSeries({ region, city, sort }) {
  const { data, isLoading } = useQuery({
    queryKey: ["event-series", region, city],
    queryFn: async ({ pageParam = 0 }) => {
      console.log("useGetEventSeries", region, city);
      const res = await fetchClient.GET("/events/v1/series", {
        params: {
          query: {
            region,
            city,
            sort: ["start,asc"],
          },
        },
      });
      return res.data?.data?.content || [];
    },
  });

  return {
    series: data,
    isLoading,
  };
}

export function useSubscribeToWaitlist() {
  const invalidateEvent = useInvalidateEvent();

  return rqClient.useMutation("post", "/events/v1/events/{id}/waitlist", {
    onSuccess: (data, variables) => {
      const {
        params: {
          path: { id: eventId },
          query: { customerId },
        },
      } = variables;

      invalidateEvent(eventId, customerId);

      queryClient.invalidateQueries({
        queryKey: ["events"],
      });
    },
  });
}

export function useLeaveWaitlist() {
  const invalidateEvent = useInvalidateEvent();

  return rqClient.useMutation("delete", "/events/v1/events/{id}/waitlist", {
    onSuccess: (data, variables) => {
      const {
        params: {
          path: { id: eventId },
          query: { customerId },
        },
      } = variables;
      invalidateEvent(eventId, customerId);

      queryClient.invalidateQueries({
        queryKey: ["events"],
      });
    },
  });
}
